import React, { useMemo, useState } from "react";
import ReusableTable from "../ReusableTable";
import { useSearchParams } from "react-router-dom";
import { AddMedication } from "../modals/AddMedication";
import { WarningModal } from "../modals/WarningModal";
import { DeleteIcon } from "../icons/DeleteIcon.jsx";
import { deleteMedicationItem } from "../../services/medication";
import toast from "react-hot-toast";

export const UserMedicationList = ({ group, isLoading, reload, userId }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showAddMedicationModal, setShowAddMedicationModal] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [openWarning, setOpenWarning] = useState(false);
  const [selectedMedicationIndex, setSelectedMedicationIndex] = useState(null);
  const [isDeletingMedication, setIsDeletingMedication] = useState(false);

  const onDeleteMedication = (index) => {
    setSelectedMedicationIndex(index);
    setWarningMessage("Are you sure you want to delete this medication?");
    setOpenWarning(true);
  };

  const warningAction = async () => {
    // Validate required props
    if (!userId) {
      toast.error("User ID is required for deletion");
      setOpenWarning(false);
      return;
    }

    // Capture current state to prevent race conditions
    const currentIndex = selectedMedicationIndex;
    const medicationToDelete = group?.medications?.[currentIndex];

    if (!medicationToDelete) {
      toast.error("Medication not found");
      setOpenWarning(false);
      return;
    }

    setIsDeletingMedication(true);
    try {
      // Call the actual backend API
      await deleteMedicationItem(userId, group.id, medicationToDelete.id);

      toast.success("Medication deleted successfully");

      // Reload the medication group to get updated data
      reload();
    } catch (error) {
      console.error("Error deleting medication:", error);
      toast.error("Failed to delete medication");
    } finally {
      setIsDeletingMedication(false);
      setSelectedMedicationIndex(null);
      setOpenWarning(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
      },
      {
        accessorKey: "instructions",
        header: "Instructions",
      },
      {
        accessorKey: "dose",
        header: "Dosage",
      },

      {
        accessorKey: "quantity",
        header: "Quantity",
      },
      {
        accessorKey: "form",
        header: "Form",
      },
      {
        accessorKey: "adminType",
        header: "Admin Type",
      },
      {
        accessorKey: "route",
        header: "Route",
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="table-actions">
            <button
              className="action-btn action-btn--delete"
              onClick={() => onDeleteMedication(row.index)}
              data-tooltip="Delete medication"
            >
              <DeleteIcon />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const pageIndex = useMemo(() => {
    const pageParam = searchParams.get("page");
    const page = parseInt(pageParam, 10);
    return isNaN(page) ? 0 : Math.max(0, page);
  }, [searchParams]);

  const updatePage = (newPageIndex) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("page", newPageIndex);
      return params;
    });
  };
  return (
    <div>
      <div className="user__top">
        <div>
          <button
            className="btn btn__primary"
            onClick={() => setShowAddMedicationModal(true)}
          >
            + Add medication
          </button>
        </div>
      </div>
      <ReusableTable
        data={group.medications}
        columns={columns}
        isLoading={isLoading || isDeletingMedication}
        pageIndex={pageIndex}
        emptyText="No medication found"
        onPageChange={updatePage}
      />

      {showAddMedicationModal && (
        <AddMedication
          isOpen={showAddMedicationModal}
          onClose={() => setShowAddMedicationModal(false)}
          reload={reload}
          group={group}
        />
      )}

      {openWarning && (
        <WarningModal
          isOpen={openWarning}
          onCancel={() => setOpenWarning(false)}
          title="Warning"
          isLoading={isDeletingMedication}
          message={warningMessage}
          onConfirm={warningAction}
        />
      )}
    </div>
  );
};
